import { useAuthStore } from "../state/auth";

/**
 * Auth headers for the `/api/*` routes (the ones outside the `/api/v1` REST
 * surface that `services/api.ts` covers with its ky client).
 *
 * Mirrors what services/hospitals.ts, services/dashboard.ts and services/reports.ts
 * each build by hand. The Bearer JWT is what authorises the request; the
 * `x-user-email` header is informational only — the server stopped accepting
 * it as proof of identity on 2026-09-30 (it required no password).
 *
 * Read through `useAuthStore.getState()` rather than the hook, so this is
 * callable from plain service functions and not only from components.
 */
export function authHeaders(json = false): Record<string, string> {
  const { user, token } = useAuthStore.getState();
  const headers: Record<string, string> = {};

  if (json) headers["Content-Type"] = "application/json";
  if (user?.email) headers["x-user-email"] = user.email;

  // Prefer the in-memory token; fall back to the persisted one so a page opened
  // before the store rehydrates still authenticates.
  const bearer = token || localStorage.getItem("authToken");
  if (bearer) headers["Authorization"] = `Bearer ${bearer}`;

  const csrf = localStorage.getItem("csrfToken");
  if (csrf) headers["x-csrf-token"] = csrf;

  return headers;
}

/**
 * Reads the error body the API routes return (`{ error: "..." }`) and turns it
 * into a thrown Error. Falls back to the status text when the body is not JSON,
 * which is what an edge/proxy error looks like.
 */
export async function throwOnError(response: Response, fallback: string): Promise<void> {
  if (response.ok) return;
  let message = fallback;
  try {
    const body = await response.json();
    message = body?.error || body?.detail || fallback;
  } catch {
    message = response.statusText || fallback;
  }
  throw new Error(message);
}
